'use client';

import {
    lazy,
    Suspense,
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type PropsWithChildren,
    type ReactElement,
} from 'react';
import type { EventCourseSubCategoryTypeId } from '@/lib/event-course-category';
import {
    getLocationsForEventCourseSubcategoryType,
    type EventCourseCategoryCounts,
} from '@/lib/event-course-category-counts';
import type { NavigationPillar } from '@/lib/navigation-pillars';

const EventCourseSubcategoryLocationsDrawer = lazy(
    () => import('@/components/carousel-square-card/event-course-subcategory-locations-drawer')
);

type EventCourseCategoryCountsContextValue = {
    pillar: NavigationPillar | null | undefined;
    counts: EventCourseCategoryCounts | undefined;
    drawerOpen: boolean;
    selectedType: EventCourseSubCategoryTypeId | null;
    openLocationsDrawer: (type: EventCourseSubCategoryTypeId) => void;
};

const EventCourseCategoryCountsContext = createContext<EventCourseCategoryCountsContextValue | null>(null);

export function EventCourseCategoryCountsProvider({
    pillar,
    counts,
    children,
}: PropsWithChildren<{
    pillar: NavigationPillar | null | undefined;
    counts: EventCourseCategoryCounts | undefined;
}>): ReactElement {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerLoaded, setDrawerLoaded] = useState(false);
    const [selectedType, setSelectedType] = useState<EventCourseSubCategoryTypeId | null>(null);
    const locations = useMemo(
        () => (selectedType ? getLocationsForEventCourseSubcategoryType(counts, pillar, selectedType) : []),
        [counts, pillar, selectedType]
    );

    const openLocationsDrawer = useCallback((type: EventCourseSubCategoryTypeId) => {
        setSelectedType(type);
        setDrawerLoaded(true);
        setDrawerOpen(true);
    }, []);

    const value = useMemo(
        () => ({ pillar, counts, drawerOpen, selectedType, openLocationsDrawer }),
        [pillar, counts, drawerOpen, selectedType, openLocationsDrawer]
    );

    return (
        <EventCourseCategoryCountsContext.Provider value={value}>
            {children}
            {drawerLoaded ? (
                <Suspense fallback={null}>
                    <EventCourseSubcategoryLocationsDrawer
                        open={drawerOpen}
                        onOpenChange={setDrawerOpen}
                        locations={locations}
                    />
                </Suspense>
            ) : null}
        </EventCourseCategoryCountsContext.Provider>
    );
}

export function useEventCourseCategoryCounts(): EventCourseCategoryCountsContextValue | null {
    return useContext(EventCourseCategoryCountsContext);
}
